/**
 * utils.ts — Pure functions to transform data between the API and React Flow.
 * No side effects, no state, no hooks.
 */
import {
  TRIGGER_ID,
  EDGE_NORMAL, EDGE_OPTION, EDGE_FALLBACK, EDGE_UP_TO_DATE, EDGE_TRIGGER_STYLE,
  MARKER_NORMAL, MARKER_OPTION, MARKER_FALLBACK, MARKER_UP_TO_DATE, MARKER_TRIGGER,
} from './constants'
import { withoutTerminalLinks } from '@/components/chatflow/actions'
import { toFirmwareTimeoutMinutes } from '@/components/chatflow/firmwareTimeout'

/**
 * Serializes a React Flow node to the format expected by the API.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function toNodePayload(rawData: any) {
  const data = withoutTerminalLinks(rawData)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const emptyToNull = (v: any) => (v === '' || v === undefined ? null : v)
  const retries = parseInt(data.fallbackMaxRetries, 10)
  const isFirmwareUpdate = data.action === 'updateFirmware'

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const payload: any = {
    nodeId:          data.nodeId,
    name:            data.name || '',
    messages:        data.messages || [''],
    options:         (data.options || [])
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((o: any) => o.label)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((o: any) => ({ label: o.label, nextNodeId: o.nextNodeId || '' })),
    action:          emptyToNull(data.action),
    nextNodeId:      emptyToNull(data.nextNodeId),
    fallbackMessage: emptyToNull(data.fallbackMessage),
    fallbackNodeId:  emptyToNull(data.fallbackNodeId),
    firmwareUpToDateNodeId: emptyToNull(data.firmwareUpToDateNodeId),
    firmwareTargetType: isFirmwareUpdate ? emptyToNull(data.firmwareTargetType) : null,
    firmwareTimeoutMinutes: isFirmwareUpdate ? toFirmwareTimeoutMinutes(data.firmwareTimeoutMinutes) : null,
  }

  if (!isFirmwareUpdate && Number.isFinite(retries) && retries >= 1) payload.fallbackMaxRetries = retries
  if (data.optionsMessageIndex != null)          payload.optionsMessageIndex = data.optionsMessageIndex

  return payload
}

/**
 * Converts API nodes to React Flow node format.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function apiNodesToFlow(apiNodes: any[], startNodeId: string | null) {
  // All nodes are rendered as 'step'. The data.action field drives visual differentiation.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return apiNodes.map(withoutTerminalLinks).map((n: any, i: number) => ({
    id:       n.nodeId,
    type:     'step',
    position: n.position || { x: 300, y: 200 + i * 200 },
    data: {
      nodeId:             n.nodeId,
      name:               n.name || '',
      messages:           n.messages || [''],
      options:            n.options || [],
      action:             n.action,
      nextNodeId:              n.nextNodeId,
      fallbackMessage:         n.fallbackMessage,
      fallbackNodeId:          n.fallbackNodeId,
      firmwareUpToDateNodeId:  n.firmwareUpToDateNodeId,
      firmwareTargetType:      n.firmwareTargetType ?? null,
      firmwareTimeoutMinutes:  n.firmwareTimeoutMinutes ?? null,
      fallbackMaxRetries:      n.fallbackMaxRetries,
      isStart:            n.nodeId === startNodeId,
      _mongoId:           n._id,
    },
  }))
}

/**
 * Builds React Flow edges from API nodes.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function buildEdges(apiNodes: any[], startNodeId: string | null) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const edges: any[] = []

  if (startNodeId) {
    edges.push({
      id:           `trigger->${startNodeId}`,
      source:       TRIGGER_ID,
      target:       startNodeId,
      sourceHandle: 'next',
      type:         'smoothstep',
      style:        EDGE_TRIGGER_STYLE,
      markerEnd:    MARKER_TRIGGER,
    })
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  apiNodes.map(withoutTerminalLinks).forEach((n: any) => {
    // Edge to next step (no options)
    if (n.nextNodeId && (!n.options || n.options.length === 0)) {
      edges.push({
        id: `${n.nodeId}->next->${n.nextNodeId}`,
        source: n.nodeId, target: n.nextNodeId,
        sourceHandle: 'next', type: 'smoothstep',
        style: EDGE_NORMAL, markerEnd: MARKER_NORMAL,
      })
    }

    // Option edges
    if (n.options && n.options.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      n.options.forEach((opt: any, i: number) => {
        if (opt.nextNodeId) {
          edges.push({
            id: `${n.nodeId}->opt${i}->${opt.nextNodeId}`,
            source: n.nodeId, target: opt.nextNodeId,
            sourceHandle: `opt-${i}`, type: 'smoothstep',
            style: EDGE_OPTION, markerEnd: MARKER_OPTION,
          })
        }
      })
    }

    // Fallback edge
    if (n.fallbackNodeId) {
      edges.push({
        id: `${n.nodeId}->fb->${n.fallbackNodeId}`,
        source: n.nodeId, target: n.fallbackNodeId,
        sourceHandle: 'fallback', type: 'smoothstep',
        style: EDGE_FALLBACK, markerEnd: MARKER_FALLBACK,
      })
    }

    // Firmware up-to-date edge (only for updateFirmware action nodes)
    if (n.firmwareUpToDateNodeId) {
      edges.push({
        id: `${n.nodeId}->utd->${n.firmwareUpToDateNodeId}`,
        source: n.nodeId, target: n.firmwareUpToDateNodeId,
        sourceHandle: 'up-to-date', type: 'smoothstep',
        style: EDGE_UP_TO_DATE, markerEnd: MARKER_UP_TO_DATE,
      })
    }
  })

  return edges
}
